import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Button} from '@wandb/weave/components/Button';
import {capitalizeFirst} from '@wandb/weave/core/util/string';
import React, {useCallback, useMemo} from 'react';
import {Slide} from 'react-toastify';
import {twMerge} from 'tailwind-merge';

import {useUpsertWorkspaceTemplateMutation} from '../../generated/graphql';
import {trackWorkspaceTemplate} from '../../services/analytics/workspaceTemplateSettings';
import {propagateErrorsContext} from '../../util/errors';
import {InstrumentedCenteredWaveLoader} from '../utility/InstrumentedLoader';
import {
  useLinePlotSettings,
  useWorkspaceLayoutSettings,
} from '../WorkspaceDrawer/Settings/hooks';
import {
  type LinePlotSettings,
  type WorkspaceLayoutSettings,
} from '../WorkspaceDrawer/Settings/types';
import {
  LINE_PLOT_TEMPLATE_FIELDS,
  TW_DRAWER_WIDTH,
  WORKSPACE_LAYOUT_TEMPLATE_FIELDS,
} from './constants';
import {useTemplateSections} from './useTemplateSections';
import {extractSettingsFromSections} from './utils';
import {useWorkspaceTemplateDrawerContext} from './WorkspaceTemplateDrawerContextProvider';
import {
  type LinePlotTemplateSettings,
  type WorkspaceLayoutTemplateSettings,
  type WorkspaceTemplate,
} from './workspaceTemplatesQueries';

export const WorkspaceTemplateContentFooter = ({
  setError,
}: {
  setError: (error: string | undefined) => void;
}) => {
  const {
    entityOrgId,
    isLoadingWorkspaceTemplates,
    refetchWorkspaceTemplates,
    workspaceTemplateMode,
    workspaceTemplates,
    closeWorkspaceTemplateDrawer,
  } = useWorkspaceTemplateDrawerContext();
  // For the time being, we only support 1 template per user within an org
  // This should be updated to support multiple templates in the future
  const viewerWorkspaceTemplate = workspaceTemplates[0];

  const linePlot = useLinePlotSettings();
  const workspaceLayout = useWorkspaceLayoutSettings();

  const [upsertWorkspaceTemplate, {loading: isUpsertingWorkspaceTemplate}] =
    useUpsertWorkspaceTemplateMutation();

  const {linePlotSettings, workspaceLayoutSettings} = useTemplateSettingsValues(
    {
      workspaceTemplate: viewerWorkspaceTemplate,
      currWorkspaceLinePlotSettings: linePlot,
      currWorkspaceLayoutSettings: workspaceLayout,
    }
  );

  const isLoading = isLoadingWorkspaceTemplates || isUpsertingWorkspaceTemplate;

  // Check which settings have changed from the existing template
  // Compare what we're about to save (linePlotSettings/workspaceLayoutSettings)
  // with what's in the template, not the raw workspace settings
  const hasLinePlotChanged = useMemo(
    () =>
      !LINE_PLOT_TEMPLATE_FIELDS.every(
        key =>
          linePlotSettings[key] ===
          viewerWorkspaceTemplate?.linePlotSettings?.[key]
      ),
    [linePlotSettings, viewerWorkspaceTemplate?.linePlotSettings]
  );

  const hasWorkspaceLayoutChanged = useMemo(() => {
    // Check if there are any actual workspace layout values to compare
    // (either in current settings or in the template)
    const hasCurrentValues = WORKSPACE_LAYOUT_TEMPLATE_FIELDS.some(
      key => workspaceLayoutSettings[key] != null
    );
    const hasTemplateValues = WORKSPACE_LAYOUT_TEMPLATE_FIELDS.some(
      key => viewerWorkspaceTemplate?.workspaceLayoutSettings?.[key] != null
    );

    // If neither has values, nothing has changed
    if (!hasCurrentValues && !hasTemplateValues) {
      return false;
    }

    // Otherwise, check if any values differ
    return !WORKSPACE_LAYOUT_TEMPLATE_FIELDS.every(
      key =>
        workspaceLayoutSettings[key] ===
        viewerWorkspaceTemplate?.workspaceLayoutSettings?.[key]
    );
  }, [
    workspaceLayoutSettings,
    viewerWorkspaceTemplate?.workspaceLayoutSettings,
  ]);
  const onButtonClick = useCallback(async () => {
    if (entityOrgId == null) {
      return;
    }

    setError(undefined);
    try {
      await upsertWorkspaceTemplate({
        variables: {
          entityId: entityOrgId,
          // Only include settings that have changed
          linePlotTemplateId: hasLinePlotChanged
            ? viewerWorkspaceTemplate?.linePlotTemplateId
            : undefined,
          linePlotSettings: hasLinePlotChanged
            ? JSON.stringify(linePlotSettings)
            : undefined,
          workspaceLayoutTemplateId: hasWorkspaceLayoutChanged
            ? viewerWorkspaceTemplate?.workspaceLayoutTemplateId
            : undefined,
          workspaceLayoutSettings: hasWorkspaceLayoutChanged
            ? JSON.stringify(workspaceLayoutSettings)
            : undefined,
        },
        context: propagateErrorsContext(),
      });
      toast(
        `Personal settings template ${workspaceTemplateMode}d. This will be auto apply to future workspaces.`,
        {
          transition: Slide,
        }
      );
      if (workspaceTemplateMode === 'update') {
        trackWorkspaceTemplate({
          action: 'update template',
          settings: linePlotSettings,
        });
      }
      closeWorkspaceTemplateDrawer();
      refetchWorkspaceTemplates();
    } catch {
      setError(
        `Unable to ${workspaceTemplateMode} template. Please try again.`
      );
    }
    // oxlint-disable-next-line react/preserve-manual-memoization
  }, [
    entityOrgId,
    setError,
    upsertWorkspaceTemplate,
    hasLinePlotChanged,
    hasWorkspaceLayoutChanged,
    linePlotSettings,
    workspaceTemplateMode,
    closeWorkspaceTemplateDrawer,
    refetchWorkspaceTemplates,
    viewerWorkspaceTemplate?.linePlotTemplateId,
    viewerWorkspaceTemplate?.workspaceLayoutTemplateId,
    workspaceLayoutSettings,
  ]);

  // Disable button if nothing has changed
  const isButtonDisabled =
    (!hasLinePlotChanged && !hasWorkspaceLayoutChanged) || isLoading;

  return (
    <div
      className={twMerge(
        TW_DRAWER_WIDTH,
        'flex h-[80px] shrink-0',
        'px-24 py-20',
        'border-t-[1px] border-solid border-wb-border',
        'bg-wb-surface',
        '[&>*]:grow'
      )}>
      <Button
        aria-label={`${capitalizeFirst(workspaceTemplateMode)} template`}
        variant="primary"
        className="grow"
        onClick={onButtonClick}
        disabled={isButtonDisabled}>
        {isLoading ? (
          <InstrumentedCenteredWaveLoader
            name="workspace-templates-save"
            size="small"
          />
        ) : (
          capitalizeFirst(workspaceTemplateMode)
        )}
      </Button>
    </div>
  );
};

/**
 * Returns both line plot and workspace layout values to save to the workspace template.
 * Uses the new values from the current workspace settings.
 */
const useTemplateSettingsValues = ({
  workspaceTemplate,
  currWorkspaceLinePlotSettings,
  currWorkspaceLayoutSettings,
}: {
  workspaceTemplate: WorkspaceTemplate;
  currWorkspaceLinePlotSettings: LinePlotSettings;
  currWorkspaceLayoutSettings?: WorkspaceLayoutSettings;
}): {
  linePlotSettings: LinePlotTemplateSettings;
  workspaceLayoutSettings: WorkspaceLayoutTemplateSettings;
} => {
  const {linePlotTemplateSections, workspaceLayoutTemplateSections} =
    useTemplateSections({
      workspaceTemplate,
      currWorkspaceLinePlotSettings,
      currWorkspaceLayoutSettings,
    });

  return useMemo(
    () => ({
      linePlotSettings: extractSettingsFromSections<LinePlotTemplateSettings>(
        linePlotTemplateSections
      ),
      workspaceLayoutSettings:
        extractSettingsFromSections<WorkspaceLayoutTemplateSettings>(
          workspaceLayoutTemplateSections
        ),
    }),
    [linePlotTemplateSections, workspaceLayoutTemplateSections]
  );
};
